import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import { isDev, isTestServer } from '@twistopayments/design-system/environment'
import { useAtom } from 'jotai'

import { configVersionAtom } from '@twisto/base'
import { useOpen } from '@twisto/hooks'

import type { ConfigEditor } from './config-editor'
import { DevAnalyticsListener } from './dev-analytics/dev-analytics-listener'
import { getStoredConfig } from './get-stored-config'

export const useConfigEditor = (extraContent?: ReactNode) => {
  const { isOpen, handleOpen, handleClose } = useOpen()

  const configModalRef = useRef<typeof ConfigEditor>()

  const [, setConfigVersion] = useAtom(configVersionAtom)
  const reloadConfig = () => setConfigVersion((version) => version + 1)

  useEffect(() => {
    if (!isDev && !isTestServer) {
      return
    }

    import('./config-editor').then(({ ConfigEditor }) => {
      configModalRef.current = ConfigEditor
    })
  }, [])

  if (!isDev && !isTestServer) {
    return {
      openConfigEditor: undefined,
      ConfigEditor: undefined,
    }
  }

  const CurrentFlagsEditor = configModalRef.current
  const FinalConfigModal = CurrentFlagsEditor
    ? () => (
        <>
          <CurrentFlagsEditor
            opened={isOpen}
            reloadConfig={reloadConfig}
            onClose={handleClose}
          >
            {extraContent}
          </CurrentFlagsEditor>
          <DevAnalyticsListener />
        </>
      )
    : undefined

  return {
    openConfigEditor: handleOpen,
    ConfigEditor: FinalConfigModal,
    configEditorOverride: {
      language: getStoredConfig('environment')?.language as
        | GLOBALS.Language
        | undefined,
      region: getStoredConfig('environment')?.region as
        | GLOBALS.Region
        | undefined,
      featureFlags: getStoredConfig('featureFlags') as
        | Record<string, boolean | undefined>
        | undefined,
    },
  }
}
